import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

const LS_DISMISSED_KEY = "md:push:opt_in_dismissed_at";
const LS_PROMPTED_KEY = "md:push:prompted";

/** Convert a base64url-encoded VAPID key into the Uint8Array `applicationServerKey` expected by PushManager. */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

/** PWA web-push helper. Exposes:
 *   - supported: feature-detect
 *   - permission: current Notification.permission value
 *   - subscribed: whether the SW already has a subscription
 *   - subscribe()/unsubscribe(): wrappers that talk to /api/push/*
 *   - markPrompted()/wasPrompted(): UX hints so we don't double-ask
 */
export default function usePushSubscription() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ok = typeof window !== "undefined"
      && "serviceWorker" in navigator
      && "PushManager" in window
      && typeof Notification !== "undefined";
    setSupported(ok);
    if (!ok) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setSubscribed(!!sub))
      .catch(() => setSubscribed(false));
  }, []);

  const subscribe = useCallback(async () => {
    if (!supported) return { ok: false, reason: "unsupported" };
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") return { ok: false, reason: "permission_denied" };

      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const r = await api.get("/push/vapid-public-key");
        const key = r.data?.public_key;
        if (!key) return { ok: false, reason: "no_vapid_key" };
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(key),
        });
      }
      const payload = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: payload.endpoint,
        keys: payload.keys,
      });
      setSubscribed(true);
      return { ok: true };
    } catch (e) {
      return { ok: false, reason: "error", error: e };
    } finally {
      setBusy(false);
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return { ok: false };
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (!sub) {
        setSubscribed(false);
        return { ok: true };
      }
      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      await api.post("/push/unsubscribe", { endpoint }).catch(() => {});
      setSubscribed(false);
      return { ok: true };
    } finally {
      setBusy(false);
    }
  }, [supported]);

  const scheduleFirstWeekCheckin = useCallback(async (bookingId) => {
    try {
      const r = await api.post("/push/schedule-first-week-checkin", { booking_id: bookingId });
      return r.data;
    } catch (e) {
      return { ok: false, reason: "error", error: e };
    }
  }, []);

  const markPrompted = useCallback(() => {
    try {
      localStorage.setItem(LS_PROMPTED_KEY, "1");
      localStorage.setItem(LS_DISMISSED_KEY, new Date().toISOString());
    } catch (e) { console.debug("[push] markPrompted failed:", e?.message || e); }
  }, []);

  const wasPrompted = useCallback(() => {
    try { return localStorage.getItem(LS_PROMPTED_KEY) === "1"; }
    catch { return false; }
  }, []);

  return {
    supported,
    permission,
    subscribed,
    busy,
    subscribe,
    unsubscribe,
    scheduleFirstWeekCheckin,
    markPrompted,
    wasPrompted,
  };
}
